import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MultiSelect,
  DropDown,
  DatePicker,
  TextBox
} from "@etframework/ui.components";

import { UpdateButtonToContent } from "./UpdateButtonToContent";
import useRolePermissionStore from "../../store/rolePermissionStore";
import useModalEvent from "../../hooks/useModalEvent";
import { useBoardPermissions } from "../../data/queries/boardQueries";
import { useTags } from "../../data/queries/tagQueries";
import { useTaxYears } from "../../data/queries/taxReturnQueries";
import { useStages } from "../../data/queries/stageQueries";
import { useFilingStatus } from "../../hooks/useFilingStatus";
import { TaxDeadlineTabInput } from "./TaxDeadlineTabInput";

/*
  Guide for adding new fields (2025/03/14):
    example: MyField
      step1: Add a new id for MyField in the ids object
      step2: Add a new useRef for MyField (eg. MyFieldRef)
      step3: In the JSX, add your input component (just copy one of the existing ones)
        ensure
          - PropertyButtonToContent.onAdd = add(ids.MyField)
          - PropertyButtonToContent.onRemove = remove(ids.MyField)
          - The input component you added has ref={MyFieldRef}
      step4: Add the following code to the getTaskValues or getTaxReturnValues functions:
                  if(properties.current.checked[ids.MyField])
                    values.MyField = MyFieldRef.current?.getValue();
          NOTE: for values.MyField, MyField HAS to match the name in the backend
      step5: Add labels to translation files
*/

export const GridUpdateEditor = forwardRef(
  ({ currentBoardId, onRemoveAllProperties, onAnyChange }, ref) => {
    const { t, i18n } = useTranslation(["BulkUpdateTasksModal", "Shared"]);
    const { ModalEvents } = useModalEvent();

    const rolePermissions = useRolePermissionStore(
      (state) => state.rolePermissions
    );
    const { data: tags } = useTags();
    const { data: boardPermissions } = useBoardPermissions(currentBoardId);
    const { data: taxYears } = useTaxYears();
    const { data: stages } = useStages(currentBoardId);
    const { data: filingStatus } = useFilingStatus();

    const formattedBoardPermissions =
      boardPermissions?.Entities?.filter(
        (x) => x.BoardPermissionType === 0
      ).map((x) => ({
        Name: x.Name ?? x.FullName ?? x.Email,
        Id: x.Id
      })) ?? [];

    const updates = useRef({ checked: {} });

    const taskNameRef = useRef(null);
    const tagRef = useRef(null);
    const collaboratorsRef = useRef(null);
    const assigneeRef = useRef(null);
    const stageRef = useRef(null);
    const taxYearRef = useRef(null);
    const dueDateRef = useRef(null);
    const taxDeadlineRef = useRef(null);
    const filingStatusRef = useRef(null);

    const [useTaxFields, setUseTaxFields] = useState(false);
    const [updateDueDateDisabled, setUpdateDueDateDisabled] = useState(false);

    const baseId = ModalEvents.BulkUpdateTaskModalFromGrid.Id;
    const ids = {
      taskName: baseId + "_task-name-input",
      tag: baseId + "_tag-input",
      collaborators: baseId + "_collaborators-input",
      assignee: baseId + "_assignee-input",
      stage: baseId + "_stage-input",
      taxYear: baseId + "_tax-year-input",
      dueDate: baseId + "_due-date-input",
      taxDeadline: baseId + "_tax-deadline-input",
      filingStatus: baseId + "_filing-status-input"
    };

    const remove = (property) => {
      updates.current.checked[property] = false;
      const anyActive = Object.values(updates.current.checked).includes(true);

      if (!anyActive) onRemoveAllProperties?.();
      else _onAnyChange?.({});
    };

    const add = (property) => {
      updates.current.checked[property] = true;
      _onAnyChange?.({});
    };

    const getValue = () => {
      return { ...getTaskValues(), ...getTaxReturnInfoValues() };
    };

    const getTaskValues = () => {
      let values = {};

      if (updates.current.checked[ids.taskName])
        values.Name = taskNameRef.current?.getValue();

      if (updates.current.checked[ids.dueDate]) {
        values.DueDate = dueDateRef.current?.getValue();
        setUpdateDueDateDisabled(true);
      } else {
        setUpdateDueDateDisabled(false);
      }

      if (updates.current.checked[ids.assignee]) {
        let value = assigneeRef.current?.getValue();

        if (value === "null") value = null;

        values.AssigneeId = value;
      }

      if (updates.current.checked[ids.stage])
        values.StageId = stageRef.current?.getValue();

      if (updates.current.checked[ids.tag])
        values.Tags_Add = tagRef.current
          ?.getValue()
          .map((tag) => ({ Title: tag.Title }));

      if (updates.current.checked[ids.collaborators])
        values.Collaborators_Add = collaboratorsRef.current?.getValue();

      if (updates.current.checked[ids.taxDeadline]) {
        const taxDeadlineValue = taxDeadlineRef.current?.getValue();

        if (taxDeadlineValue?.value?.updateDueDate) {
          switch (taxDeadlineValue?.action) {
            case TaxDeadlineTabInput.actions.SpecificDate.name:
              values.DueDate = taxDeadlineValue?.value?.dateValue;
              break;
            case TaxDeadlineTabInput.actions.ApplySpecificExtension.name:
              values.DueDate = taxDeadlineValue?.value?.dateValue;
              break;
            case TaxDeadlineTabInput.actions.ApplyExtension.name:
              values.DueDate_ApplyExtension = null;
              break;
            case TaxDeadlineTabInput.actions.RemoveExtension.name:
              values.DueDate_RemoveExtension = null;
              break;
          }
        }
      }

      return values;
    };

    const getTaxReturnInfoValues = () => {
      let values = {};

      if (updates.current.checked[ids.taxYear])
        values.TaxYear = taxYearRef.current?.getValue();

      if (updates.current.checked[ids.taxDeadline]) {
        const taxDeadlineValue = taxDeadlineRef.current?.getValue();

        switch (taxDeadlineValue?.action) {
          case TaxDeadlineTabInput.actions.SpecificDate.name:
            values.TaxDeadline = taxDeadlineValue?.value?.dateValue;
            break;
          case TaxDeadlineTabInput.actions.ApplySpecificExtension.name:
            values.ExtendedTaxDeadline = taxDeadlineValue?.value?.dateValue;
            break;
          case TaxDeadlineTabInput.actions.ApplyExtension.name:
            values.ExtendedTaxDeadline_ApplyExtension = null;
            break;
          case TaxDeadlineTabInput.actions.RemoveExtension.name:
            values.ExtendedTaxDeadline = null;
            break;
        }
      }

      if (updates.current.checked[ids.filingStatus])
        values.FilingStatus = filingStatusRef.current?.getValue();

      return values;
    };

    const _onAnyChange = (e) => {
      const values = getValue();
      onAnyChange?.({
        ...e,
        properties: updates.current.checked,
        value: values,
        valid: !Object.entries(values).find(([key, value]) => {
          return (
            key !== "ExtendedTaxDeadline" &&
            ((value === null && key !== "AssigneeId") ||
              value === undefined ||
              (Array.isArray(value) && !value.length))
          );
        })
      });
    };

    const showTAXRelatedFields = () => setUseTaxFields(true);
    const hideTAXRelatedFields = () => setUseTaxFields(false);

    useImperativeHandle(ref, () => ({
      getValue,
      getTaskValues,
      getTaxReturnInfoValues,
      showTAXRelatedFields,
      hideTAXRelatedFields
    }));

    return (
      <div className="d-flex flex-column gap-3">
        <UpdateButtonToContent
          label={t("UpdateEditor.Tags")}
          htmlFor={ids.tag}
          iconCss={"fa-regular fa-plus"}
          onAdd={() => add(ids.tag)}
          onRemove={() => remove(ids.tag)}
        >
          <MultiSelect
            ref={tagRef}
            dataSource={tags ?? []}
            fields={{
              text: "Title",
              value: "Id"
            }}
            id={ids.tag}
            allowCustomValues
            allowObjectBinding
            locale={i18n?.language}
            change={_onAnyChange}
            allowFiltering
          />
        </UpdateButtonToContent>

        <UpdateButtonToContent
          label={t("UpdateEditor.Collaborators")}
          htmlFor={ids.collaborators}
          iconCss={"fa-regular fa-plus"}
          onAdd={() => add(ids.collaborators)}
          onRemove={() => remove(ids.collaborators)}
        >
          <MultiSelect
            ref={collaboratorsRef}
            dataSource={formattedBoardPermissions}
            fields={{
              text: "Name",
              value: "Id"
            }}
            id={ids.collaborators}
            allowObjectBinding
            locale={i18n?.language}
            change={_onAnyChange}
            allowFiltering
          />
        </UpdateButtonToContent>

        <UpdateButtonToContent
          label={t("UpdateEditor.TaskName")}
          iconCss={"fa-regular fa-pencil"}
          onAdd={() => add(ids.taskName)}
          onRemove={() => remove(ids.taskName)}
        >
          <TextBox
            ref={taskNameRef}
            name="Name"
            className="grow"
            id={ids.taskName}
            change={_onAnyChange}
          />
        </UpdateButtonToContent>

        <UpdateButtonToContent
          label={t("UpdateEditor.Assignee")}
          iconCss={"fa-regular fa-pencil"}
          onAdd={() => add(ids.assignee)}
          onRemove={() => remove(ids.assignee)}
        >
          <DropDown
            ref={assigneeRef}
            name="AssigneeId"
            className="grow"
            dataSource={[
              { Id: "null", Name: t("Unassigned") },
              ...formattedBoardPermissions
            ]}
            fields={{
              text: "Name",
              value: "Id"
            }}
            id={ids.assignee}
            locale={i18n?.language}
            change={_onAnyChange}
            sortOrder="Ascending"
            allowFiltering
          />
        </UpdateButtonToContent>

        {rolePermissions?.CanComplete && (
          <UpdateButtonToContent
            label={t("UpdateEditor.Stage")}
            iconCss={"fa-regular fa-pencil"}
            onAdd={() => add(ids.stage)}
            onRemove={() => remove(ids.stage)}
          >
            <DropDown
              ref={stageRef}
              name="StageId"
              className="grow"
              dataSource={stages}
              fields={{
                text: "Title",
                value: "Id"
              }}
              id={ids.stage}
              locale={i18n?.language}
              change={_onAnyChange}
              sortOrder="Ascending"
              allowFiltering
            />
          </UpdateButtonToContent>
        )}

        {useTaxFields && (
          <UpdateButtonToContent
            label={t("UpdateEditor.TaxYear")}
            iconCss={"fa-regular fa-pencil"}
            onAdd={() => add(ids.taxYear)}
            onRemove={() => remove(ids.taxYear)}
          >
            <DropDown
              ref={taxYearRef}
              name="TaxYear"
              className="grow"
              dataSource={taxYears}
              fields={{
                text: "Year",
                value: "Year"
              }}
              id={ids.assignee}
              locale={i18n?.language}
              change={_onAnyChange}
              sortOrder="Ascending"
              allowFiltering
            />
          </UpdateButtonToContent>
        )}

        {useTaxFields && (
          <UpdateButtonToContent
            label={t("UpdateEditor.FilingStatus")}
            iconCss={"fa-regular fa-pencil"}
            onAdd={() => add(ids.filingStatus)}
            onRemove={() => remove(ids.filingStatus)}
          >
            <DropDown
              ref={filingStatusRef}
              name="FillingStatus"
              className="grow"
              dataSource={filingStatus}
              fields={{
                text: "Title",
                value: "Id"
              }}
              id={ids.filingStatus}
              locale={i18n?.language}
              change={_onAnyChange}
            />
          </UpdateButtonToContent>
        )}

        <UpdateButtonToContent
          label={t("UpdateEditor.DueDate")}
          iconCss={"fa-regular fa-pencil"}
          htmlFor={ids.dueDate}
          onAdd={() => add(ids.dueDate)}
          onRemove={() => remove(ids.dueDate)}
        >
          <DatePicker
            id={ids.dueDate}
            ref={dueDateRef}
            width={"800px"}
            change={_onAnyChange}
            language={i18n.language}
          />
        </UpdateButtonToContent>

        {useTaxFields && (
          <UpdateButtonToContent
            label={t("UpdateEditor.TaxDeadline")}
            iconCss={"fa-regular fa-pencil"}
            htmlFor={ids.taxDeadline}
            onAdd={() => add(ids.taxDeadline)}
            onRemove={() => remove(ids.taxDeadline)}
          >
            <TaxDeadlineTabInput
              id={ids.taxDeadline}
              ref={taxDeadlineRef}
              updateDueDateDisabled={updateDueDateDisabled}
              change={_onAnyChange}
              changeAction={() => {
                //scroll down to make sure user sees any validation messages
                setTimeout(() => {
                  document
                    .getElementById(ids.taxDeadline)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
              }}
            />
          </UpdateButtonToContent>
        )}
      </div>
    );
  }
);
